import type { ReactNode, MouseEvent } from 'react'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import type { EkycStatus } from '@brushfire/types'
import { ekycStatusLabel } from '../lib/ekycUi'
import { RiCheckLine, RiErrorWarningFill } from '@/shared/icons/remix'
import { palettePrimaryMain, paletteSuccessMain } from '@/theme/designTokens'

interface Props {
  status: EkycStatus | null | undefined
  onAvatarClick: (event: MouseEvent<HTMLElement>) => void
  menuOpen: boolean
  children: ReactNode
}

const UNVERIFIED = '#F59E0B'

/**
 * Nav profile avatar with top-right eKYC badge only (no status ring).
 * Entire control (avatar + badge) opens the account menu.
 */
export function EkycAvatarStatus({
  status,
  onAvatarClick,
  menuOpen,
  children,
}: Readonly<Props>) {
  const verified = status === 'verified'
  const label = ekycStatusLabel(status)
  const accent = verified ? paletteSuccessMain : UNVERIFIED

  return (
    <Box
      sx={{
        position: 'relative',
        width: 40,
        height: 40,
        flexShrink: 0,
        overflow: 'visible',
      }}
      data-testid="ekyc-avatar-status"
    >
      <IconButton
        onClick={onAvatarClick}
        aria-label={`Account menu. Identity status: ${label}`}
        aria-controls={menuOpen ? 'provider-nav-profile-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={menuOpen ? 'true' : undefined}
        data-testid="provider-nav-avatar"
        sx={{
          p: 0,
          overflow: 'visible',
          '&:hover': { bgcolor: 'transparent' },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: 40,
            height: 40,
            overflow: 'visible',
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              boxSizing: 'border-box',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: palettePrimaryMain,
              border: `2px solid ${accent}`,
              color: '#fff',
              fontSize: '12px',
              fontWeight: 600,
              lineHeight: '24px',
            }}
          >
            {children}
          </Box>

          <Box
            component="span"
            data-testid="ekyc-avatar-badge"
            aria-hidden
            sx={{
              position: 'absolute',
              right: -3,
              top: -3,
              width: 18,
              height: 18,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: verified ? paletteSuccessMain : 'transparent',
              color: verified ? '#ffffff' : accent,
              border: verified ? '2px solid #ffffff' : 'none',
              boxSizing: 'border-box',
              lineHeight: 0,
              zIndex: 1,
              boxShadow: verified ? '0 1px 2px rgba(0,0,0,0.2)' : undefined,
              filter: verified
                ? undefined
                : 'drop-shadow(0 0 1px #FFFFFF) drop-shadow(0 0 1px #FFFFFF) drop-shadow(0 1px 2px rgba(0,0,0,0.35))',
            }}
          >
            {verified ? (
              <RiCheckLine size={12} aria-hidden />
            ) : (
              <RiErrorWarningFill size={20} aria-hidden />
            )}
          </Box>
        </Box>
      </IconButton>
    </Box>
  )
}
